import React from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  Box, Typography, Button, Paper, Table, TableHead, TableRow, TableCell,
  TableBody, Chip, IconButton, Dialog, DialogTitle, DialogContent,
  DialogContentText, DialogActions, MenuItem, Select, FormControl,
  InputLabel, Skeleton, Tooltip, Alert,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import PersonIcon from '@mui/icons-material/Person';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import { useAuth, DEFAULT_PARTNER_CONTEXT, RESERVED_VECTROS_ADMIN_CONTEXT } from '../../auth/AuthContext';
import { listScopedKeys, revokeScopedKey } from '../../api/partnerApi';
import { listAppContexts } from '../../api/developerApi';

// ---------------------------------------------------------------------------
// ScopedKeysList — read-side of the Scoped Keys tab.
//
// Scoped keys are CONTEXT-SCOPED. The `/v1/admin/keys/scoped` GET filters to the
// caller token's context (R1-01/#242), so a token confined to one context can't
// see a key bound to another — the #387 bug. We fix it the way the CLI does
// (#442): a CONTEXT selector picks one of the OWNER's own contexts, and we mint
// a token PINNED to it (via partnerApi → getPartnerApiToken(env, context)) so
// list + revoke operate inside that context. Switching context re-mints +
// refetches. Defaults to the base DEFAULT_PARTNER_CONTEXT ('default'); the
// create wizard's onCreated handoff switches the view to a freshly-minted
// key's context so it's immediately visible (closing create→list→revoke).
//
// The reserved control-plane context is excluded from the picker: the partner
// API rejects an explicit mint pinned to it outright, so list/revoke could
// never work for it anyway (mirrors the same exclusion in the create wizard's
// context picker).
//
// env + context are CONTROLLED by the parent (ScopedKeysTab) so the wizard
// handoff can drive them; the server enforces tenant binding on the pinned
// token, so context only selects WHICH of the caller's contexts — never a tenant.
// ---------------------------------------------------------------------------

// Status is stored lowercase ('active'/'revoked'); compare case-insensitively
// to guard any legacy uppercase rows (mirrors the Standard Keys tab).
const isActiveStatus = (status) => (status || '').toLowerCase() === 'active';

function UserTypeChip({ userType }) {
  if (userType === 'SERVICE') {
    return (
      <Chip
        size="small"
        icon={<SmartToyIcon fontSize="small" />}
        label="SERVICE"
        sx={{ bgcolor: '#f3e8ff', color: '#6b21a8', fontWeight: 600, fontSize: 11 }}
      />
    );
  }
  // HUMAN is the default for legacy rows (default-on-read).
  return (
    <Chip
      size="small"
      icon={<PersonIcon fontSize="small" />}
      label="HUMAN"
      sx={{ bgcolor: '#dbeafe', color: '#1e40af', fontWeight: 600, fontSize: 11 }}
    />
  );
}

export default function ScopedKeysList({
  onCreateClick,
  env,
  onEnvChange,
  context,
  onContextChange,
}) {
  const { user } = useAuth();
  const qc = useQueryClient();
  // revokeTarget holds the row pending confirmation, or null if no dialog open.
  const [revokeTarget, setRevokeTarget] = React.useState(null);
  // Status filter — revoked keys accumulate and bury active ones, so default
  // to 'active' (matches the Standard Keys tab). 'all'/'revoked' stay available.
  const [statusFilter, setStatusFilter] = React.useState('active');

  const envTenantId = env === 'live' ? user?.liveTenantId : user?.testTenantId;

  // The OWNER's app contexts for the selected env — includes the base
  // DEFAULT_PARTNER_CONTEXT ('default') like any other context; the reserved
  // control-plane context is dropped (see module header).
  const { data: dataContexts } = useQuery(
    ['devAppContexts', env],
    () => listAppContexts(env),
    { enabled: !!user },
  );
  const contextOptions = React.useMemo(() => {
    const seen = new Set();
    const out = [];
    const push = (value, label) => {
      if (!value || value === RESERVED_VECTROS_ADMIN_CONTEXT || seen.has(value)) return;
      seen.add(value);
      out.push({ value, label });
    };
    (dataContexts || []).forEach((c) =>
      push(c.contextId, c.name ? `${c.contextId} — ${c.name}` : c.contextId));
    return out;
  }, [dataContexts]);

  // Keys in the SELECTED (env, context). The token is minted pinned to context,
  // so the list already scopes to it; the tenant filter below is defensive.
  const { data: allKeys, isLoading, error } = useQuery(
    ['scopedKeys', env, context],
    () => listScopedKeys(env, context),
    { enabled: !!user && !!context },
  );

  const revokeMutation = useMutation(
    // Pin the revoke token to the KEY's own context (equals the selected context,
    // since the list only shows that context's keys) — mirrors `vectros key
    // revoke --context`. Falls back to the selected context defensively.
    ({ keyId, contextId }) => revokeScopedKey(env, contextId || context, keyId),
    {
      onSuccess: () => {
        qc.invalidateQueries(['scopedKeys']);
        setRevokeTarget(null);
      },
    },
  );

  // Defensive: keep only the selected env's tenant (the pinned token already
  // scopes to it, so this should be a no-op — guards a wire-shape surprise).
  const tenantKeys = (allKeys || []).filter((k) => k.tenantId === envTenantId);

  // Anything not active is treated as revoked (matches the Status chip's
  // green-for-active / red-otherwise rule below). Compare case-insensitively so
  // the two key tabs can't drift on status casing (the Standard tab's bug).
  const keys = tenantKeys.filter((k) => {
    if (statusFilter === 'active') return isActiveStatus(k.status);
    if (statusFilter === 'revoked') return !isActiveStatus(k.status);
    return true;
  });

  return (
    <Box>
      {/* Header row: env + context selectors + New button */}
      <Box sx={{
        display: 'flex',
        alignItems: { xs: 'flex-start', sm: 'center' },
        justifyContent: 'space-between',
        flexDirection: { xs: 'column', sm: 'row' },
        gap: 2,
        mb: 3,
      }}>
        <Typography variant="body2" color="text.secondary">
          Short-lived, scope-limited keys (<code>ssk_live_*</code> / <code>ssk_test_*</code>)
          bound to a PartnerUser + AccessProfile. Use for agents, CI tooling, and
          per-team-member bots. Keys are scoped to an app context — pick a context
          to manage its keys.
        </Typography>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 110 }}>
            <InputLabel>Environment</InputLabel>
            <Select
              value={env}
              label="Environment"
              onChange={(e) => {
                // A context can differ between live/test tenants — reset to the
                // always-present admin context so we never query an (env, context)
                // pair that doesn't exist. The wizard handoff sets both directly,
                // bypassing this reset.
                onEnvChange(e.target.value);
                onContextChange(DEFAULT_PARTNER_CONTEXT);
              }}
            >
              <MenuItem value="live">Live</MenuItem>
              <MenuItem value="test">Test</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Context</InputLabel>
            <Select
              value={context}
              label="Context"
              onChange={(e) => onContextChange(e.target.value)}
            >
              {/* Always offer the selected context even if the data-context list
                  hasn't loaded (or doesn't include it) so the value stays valid. */}
              {!contextOptions.some((o) => o.value === context) && (
                <MenuItem value={context}>{context}</MenuItem>
              )}
              {contextOptions.map((o) => (
                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="revoked">Revoked</MenuItem>
              <MenuItem value="all">All</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={onCreateClick}
            disabled={!onCreateClick}
            sx={{
              bgcolor: '#3B82F6',
              '&:hover': { bgcolor: '#2563EB' },
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            New scoped key
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error.response?.data?.message || error.message || 'Failed to load scoped keys'}
        </Alert>
      )}

      <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflowX: 'auto' }}>
        <Table size="small">
          <TableHead sx={{ bgcolor: 'background.default' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Name</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>User</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Context</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>Created</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading
              ? Array.from({ length: 3 }).map((_, i) => (
                <TableRow key={i}>
                  {Array.from({ length: 6 }).map((_, j) => (
                    <TableCell key={j}><Skeleton /></TableCell>
                  ))}
                </TableRow>
              ))
              : keys.map((key) => (
                <TableRow key={key.keyId} hover>
                  <TableCell sx={{ fontSize: 13 }}>{key.keyName}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" sx={{ fontSize: 12, fontFamily: 'monospace', color: '#64748b' }}>
                        {key.userId}
                      </Typography>
                      <UserTypeChip userType={key.userType} />
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontSize: 13 }}>{key.contextId}</TableCell>
                  <TableCell>
                    <Chip
                      label={key.status}
                      size="small"
                      sx={{
                        bgcolor: isActiveStatus(key.status) ? '#dcfce7' : '#fee2e2',
                        color: isActiveStatus(key.status) ? '#166534' : '#991b1b',
                        fontWeight: 600,
                        fontSize: 11,
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ fontSize: 12, color: '#64748b' }}>
                    {key.createdAt ? new Date(key.createdAt).toLocaleDateString() : '—'}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Revoke">
                      <IconButton
                        size="small"
                        color="error"
                        aria-label="Revoke scoped key"
                        onClick={() => setRevokeTarget(key)}
                        disabled={!isActiveStatus(key.status)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            {!isLoading && keys.length === 0 && !error && (
              <TableRow>
                <TableCell colSpan={6} sx={{ p: 0, border: 0 }}>
                  <Box sx={{
                    p: 6,
                    textAlign: 'center',
                    color: 'text.secondary',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 2,
                  }}>
                    {tenantKeys.length > 0 ? (
                      // Keys exist in this (env, context) but none match the filter.
                      <Typography variant="body1" color="text.primary">
                        No {statusFilter === 'all' ? '' : `${statusFilter} `}scoped keys
                        {' '}in <strong>{context}</strong> ({env})
                      </Typography>
                    ) : (
                      <>
                        <Typography variant="body1" color="text.primary">
                          No scoped keys in <strong>{context}</strong> ({env}) yet
                        </Typography>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<AddIcon />}
                          onClick={onCreateClick}
                          disabled={!onCreateClick}
                          sx={{ textTransform: 'none' }}
                        >
                          New scoped key
                        </Button>
                      </>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      {/* Revoke confirmation dialog */}
      <Dialog
        open={!!revokeTarget}
        onClose={() => setRevokeTarget(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Revoke scoped key?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            <Box component="span" sx={{ display: 'block', mb: 1 }}>
              You're about to revoke <strong>{revokeTarget?.keyName}</strong>
              {' '}(bound to user {revokeTarget?.userId} in context {revokeTarget?.contextId}).
            </Box>
            <Box component="span" sx={{ display: 'block', color: 'warning.main' }}>
              This takes effect within ~5 minutes (Rust authorizer cache window).
              Anything currently using this key will start receiving 401 errors
              once the cache flushes.
            </Box>
          </DialogContentText>
          {revokeMutation.isError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {revokeMutation.error?.response?.data?.message
                || revokeMutation.error?.message
                || 'Revoke failed'}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setRevokeTarget(null)}
            sx={{ textTransform: 'none' }}
            disabled={revokeMutation.isLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={() => revokeMutation.mutate({ keyId: revokeTarget.keyId, contextId: revokeTarget.contextId })}
            variant="contained"
            color="error"
            disabled={revokeMutation.isLoading}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {revokeMutation.isLoading ? 'Revoking…' : 'Revoke'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
